import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClientClassificationBadges } from '@/components/clients/ClientClassificationBadges';
import type { Client } from '@/lib/types';
import type { CachedPostureAnalysis } from '@/context/clients-cache-context';
import { formatDateOnlyLocale, safeToDate } from '@/lib/utils';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import { formatDistanceToNow } from 'date-fns';
import { Mail, MonitorUp, Phone, Pencil, PersonStanding, PlusCircle } from 'lucide-react';

/**
 * Tolerant of a missing display name: clients created with only a first name can
 * lack the computed `name` field entirely (the create payload's undefined lastName
 * is dropped by JSON serialization, so the server-side name sync used to skip) —
 * the profile render must never crash on that.
 */
export function clientInitials(name: string | null | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Best available display name — unified onto the shared `clientDisplayLabel`
 * choke point (roster-fields.ts) so this header stays in sync with the
 * roster, the select-client picker, and the header widget. Kept exported
 * under this name for existing callers/tests.
 */
export function clientDisplayName(client: Client): string {
  return clientDisplayLabel(client);
}

function calculateAge(dateOfBirth: Client['dateOfBirth']): number | null {
  const birthDate = safeToDate(dateOfBirth);
  if (!birthDate) return null;
  const ageDifMs = Date.now() - birthDate.getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}

interface ClientProfileHeaderProps {
  client: Client;
  profileImage?: string;
  allPostureAnalyses?: CachedPostureAnalysis[];
  onEdit?: () => void;
  onPostureAnalysis?: () => void;
  onSopAssessment?: () => void;
  /** Experimental, desktop only: caller omits it when Remote Assessment is unavailable. */
  onRemoteAssessment?: () => void;
}

export function ClientProfileHeader({
  client,
  profileImage,
  allPostureAnalyses,
  onEdit,
  onPostureAnalysis,
  onSopAssessment,
  onRemoteAssessment,
}: ClientProfileHeaderProps) {
  const age = calculateAge(client.dateOfBirth);
  const addedAt = safeToDate(client.createdAt);
  const avatarInitials = clientInitials(clientDisplayName(client));

  return (
    <div
      className="rounded-xl border bg-card shadow-sm"
      data-testid="client-profile-header"
    >
      <div className="p-4 sm:p-6">
        {/* Client 360 — centered eyebrow. */}
        <p className="text-center text-xs font-semibold uppercase tracking-wide text-primary">
          Client 360
        </p>

        {/* Avatar (left) · name + classification (to the right of the avatar). */}
        <div className="mt-3 flex items-center gap-3 sm:gap-4">
          <Avatar className="h-16 w-16 sm:h-20 sm:w-20 border-2 border-background shadow-sm shrink-0">
            <AvatarImage src={profileImage} className="object-cover object-top" alt="" />
            <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
              {avatarInitials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-1 text-right">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{clientDisplayName(client)}</h1>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <ClientClassificationBadges
                client={client}
                allPostureAnalyses={allPostureAnalyses?.filter((a) => a.clientId === client.id)}
              />
            </div>
          </div>
        </div>

        {/* Contact + demographics. */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {client.email && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{client.email}</span>
            </span>
          )}
          {client.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3.5 w-3.5 shrink-0" />
              {client.phone}
            </span>
          )}
          {client.sex && (
            <span>
              Sex: <span className="text-foreground font-medium">{client.sex}</span>
            </span>
          )}
          {age != null && (
            <span>
              Age: <span className="text-foreground font-medium">{age}</span>
            </span>
          )}
          {client.dateOfBirth && (
            <span>
              DOB:{' '}
              <span className="text-foreground font-medium">
                {formatDateOnlyLocale(client.dateOfBirth)}
              </span>
            </span>
          )}
          {addedAt && (
            <span>
              Added{' '}
              <span className="text-foreground font-medium">
                {formatDistanceToNow(addedAt, { addSuffix: true })}
              </span>
            </span>
          )}
        </div>

        {!client.classification && (
          <Badge variant="outline" className="mt-3 text-xs">
            No classification yet — run a posture analysis or SOP assessment
          </Badge>
        )}

        {/* Actions — last. Icon-only on mobile, icon + label from sm up. */}
        <div className="mt-4 flex flex-wrap gap-2">
          {onEdit && (
            <Button type="button" variant="outline" size="sm" onClick={onEdit} aria-label="Edit client" title="Edit client">
              <Pencil className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
          {onPostureAnalysis && (
            <Button type="button" size="sm" variant="secondary" onClick={onPostureAnalysis} aria-label="Posture analysis" title="Posture analysis">
              <PersonStanding className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Posture</span>
            </Button>
          )}
          {onSopAssessment && (
            <Button type="button" size="sm" onClick={onSopAssessment} aria-label="Start assessment" title="Start assessment">
              <PlusCircle className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Assessment</span>
            </Button>
          )}
          {onRemoteAssessment && (
            <Button type="button" size="sm" variant="secondary" onClick={onRemoteAssessment} aria-label="Remote assessment" title="Remote assessment">
              <MonitorUp className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Remote</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
